/**
 * The Office.js side of the anchor tracker: every Word call it makes lives
 * here or in `OfficeDocumentBookmarks`, behind the `WordDocument` and
 * `WordSession` interfaces. All calls go through the add-in's reader-writer
 * lock in `document.ts`, including the registration of Word's change events,
 * so nothing here can interleave with a write elsewhere in the add-in.
 */
import { DocumentComments } from "@common/utils/redline/ooxml/document-comments";
import { OoxmlNumbering } from "@common/utils/redline/ooxml/ooxml-numbering";
import { OoxmlOutlineLevels } from "@common/utils/redline/ooxml/ooxml-outline-levels";
import { PackageBinaryParts } from "@common/utils/redline/ooxml/package-binary-parts";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { wordDiff } from "@common/utils/redline/text/word-diff";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";

import { bulkApplyClockMs, recordBulkApplySpan } from "../../bulk-apply-run-timeline";
import { pendingForegroundWorkCount, withWordContextRead, withWordContextWrite } from "../../document";
import { logWarning } from "../../sentry-reporter";
import { BodyReplacement, type BodyReplacementPhase } from "../body-replacement";
import { DocumentEvents, type DocumentEventSource } from "../document-events";
import type { EditedParagraphDetector } from "./edited-paragraph-detector";
import { OfficeDocumentBookmarks } from "./office-document-bookmarks";
import { TableReadCache } from "./table-read-cache";
import type { RecoveryConfig } from "../recovery-config";
import type { BodyRead, ChangeOrigin, PageParagraphRead, PageRead, ParagraphChange, RecoveryBodyRead, TableRead, Unsubscribe, WordChange, WordDocument, WordParagraph, WordSession } from "../word-document";

/** One read of the body: Word's paragraph list and the body package the reader walks, with the event stream's sequence when the read began. */
interface WordAndPackageBodyRead {
    wordParagraphs: Word.Paragraph[];
    bodyPackageXml: string;
    bodyWalk: OoxmlRange;
    coversEventsThroughSequence: number;
}

/** The Sentry log line written when Office.js and the body's OOXML count the paragraphs differently; it carries both counts, how many paragraphs paired by their text, and no text. */
const BODY_PARAGRAPH_COUNT_MISMATCH_MESSAGE = "REDLINE_BODY_PARAGRAPH_COUNT_MISMATCH";

/** The pairing's filter: a walk position Word lists a paragraph for. */
const isWordParagraph = (pairedParagraph: WordParagraph | null): pairedParagraph is WordParagraph => pairedParagraph !== null;

export class OfficeWordSession implements WordSession {
    readonly bookmarks: OfficeDocumentBookmarks;

    constructor(
        readonly context: Word.RequestContext,
        config: RecoveryConfig,
        /** Shared by every session of the document, so one table read serves every card of that table. */
        private readonly tableReads: TableReadCache,
        /** The document's event stream, where every whole-body read is published. */
        private readonly events: DocumentEvents,
    ) {
        this.bookmarks = new OfficeDocumentBookmarks(context, config);
    }

    async paragraphById(uniqueLocalId: string): Promise<WordParagraph | null> {
        const paragraph = await OfficeDocumentBookmarks.wordParagraphById(this.context, uniqueLocalId);
        /**
         * WHEN Word no longer resolves this paragraph id
         *   CONSEQUENCE
         *     Return null instead of a WordParagraph value.
         *
         * NULL
         *   The anchor is lost. Callers should stop reading fields from it.
         */
        return paragraph ? { uniqueLocalId: paragraph.uniqueLocalId, text: paragraph.text } : null;
    }

    /**
     * The body paragraphs at the core's indexes: positions in the body's OOXML walk,
     * each turned into the Word paragraph that is that paragraph
     * (`wordParagraphsByWalkIndex`).
     *
     * CASES
     *   - the index names a paragraph Word lists              -> that paragraph, with the walk's accepted text
     *   - the index names one Word lists no paragraph for     -> nothing for it
     *   - the index is past the walk's end                    -> nothing for it
     *
     * EXAMPLE
     *   indexes [2], Word lists W0 W1 W2 W3 and the walk holds the same four                 -> [W2]
     *   indexes [2], the walk holds an empty paragraph at 1 that Word does not list          -> [W1], paired by its text
     */
    async paragraphsAt(indexes: number[]): Promise<WordParagraph[]> {
        const wordParagraphByWalkIndex = await this.wordParagraphsByWalkIndex();
        return indexes.map((walkIndex) => wordParagraphByWalkIndex[walkIndex] ?? null).filter(isWordParagraph);
    }

    /**
     * The body's paragraphs with their ids and the text Word shows in them, loaded in one sync:
     * `Paragraph.text` reads a deleted word as present, as `paragraphById` reads it, so the
     * paragraph-id step scores the same text whether it finds a paragraph here or by id.
     */
    async listedParagraphs(): Promise<WordParagraph[]> {
        const wordParagraphs = this.context.document.body.paragraphs;
        wordParagraphs.load("items/uniqueLocalId,items/text");
        await this.context.sync();
        return wordParagraphs.items.map((wordParagraph) => ({ uniqueLocalId: wordParagraph.uniqueLocalId, text: wordParagraph.text }));
    }

    /** Every body paragraph Word lists, in the walk's order, with the walk's accepted text: one pass over the body for recovery by text. */
    async bodyParagraphs(): Promise<WordParagraph[]> {
        return (await this.wordParagraphsByWalkIndex()).filter(isWordParagraph);
    }

    /**
     * The whole body in one sync, described every way a recovery step reads it: Word's own
     * paragraph list with the text Word shows in each, the paragraph Word lists for each
     * position of the body walk, the reader's description of every walk paragraph
     * (`OoxmlRange.documentParagraphs`, over the body package with its comment, numbering
     * and styles parts, so each carries the ids, the accepted text and the heading path
     * every other reader of the document sees), and the package itself.
     *
     * WHY the reader's list and not the walk's texts alone
     *   A card's anchors were built from this reader on the core's side; checking them
     *   against the same reader's output here is what makes the index, the heading
     *   path and the neighbour texts comparable at all.
     */
    async readBodyForRecovery(): Promise<RecoveryBodyRead> {
        const bodyRead = await this.readWordAndPackageBody();
        const pairedStartedAtMs = bulkApplyClockMs();
        const wordParagraphByWalkIndex = OfficeWordSession.pairWalkWithWord(bodyRead);
        recordBulkApplySpan("body_read_walk_paired_with_word", pairedStartedAtMs);
        const describedStartedAtMs = bulkApplyClockMs();
        const readerParagraphs = bodyRead.bodyWalk.documentParagraphs(
            DocumentComments.fromPackage(bodyRead.bodyPackageXml),
            OoxmlNumbering.fromPackage(bodyRead.bodyPackageXml),
            OoxmlOutlineLevels.fromPackage(bodyRead.bodyPackageXml),
        );
        recordBulkApplySpan("body_read_paragraphs_described", describedStartedAtMs, { paragraphs: readerParagraphs.length });
        return {
            wordParagraphs: bodyRead.wordParagraphs.map((wordParagraph) => ({ uniqueLocalId: wordParagraph.uniqueLocalId, text: wordParagraph.text })),
            wordParagraphByWalkIndex,
            anchorParagraphs: readerParagraphs.map((readerParagraph) => ({
                walkIndex: readerParagraph.paragraphIndex,
                paragraph: wordParagraphByWalkIndex[readerParagraph.paragraphIndex] ?? null,
                paraId: readerParagraph.paraId,
                text: readerParagraph.allChangesAcceptedText,
                headingPath: readerParagraph.headingPath,
            })),
            bodyFlatOpc: bodyRead.bodyPackageXml,
            coversEventsThroughSequence: bodyRead.coversEventsThroughSequence,
        };
    }

    /**
     * One sync for the whole body: Word's paragraphs with their ids and texts, and the
     * body package the reader walks, published on the document's event stream as a
     * whole-body read before it is returned, as `readBody` publishes its own.
     */
    private async readWordAndPackageBody(): Promise<WordAndPackageBodyRead> {
        const coversEventsThroughSequence = this.events.lastEventSequence;
        const wordParagraphs = this.context.document.body.paragraphs;
        wordParagraphs.load("items/uniqueLocalId,items/text");
        const bodyOoxml = this.context.document.body.getOoxml();
        const wordSyncStartedAtMs = bulkApplyClockMs();
        await this.context.sync();
        recordBulkApplySpan("body_read_word_sync", wordSyncStartedAtMs, { paragraphs: wordParagraphs.items.length, characters: bodyOoxml.value.length });
        const preparedStartedAtMs = bulkApplyClockMs();
        /**
         * The body's package without the embedded fonts a font-embedding document adds to every
         * read: the event stream, the page store and the walk below each hold or parse this string,
         * so millions of characters of fonts in it are millions each of them holds and parses
         * (`PackageBinaryParts.withoutEmbeddedFonts`).
         *
         * EXAMPLE
         *   a contract that embeds ten fonts, one body read -> 10,678,456 characters from Word,
         *   most of them the ten font parts
         */
        const bodyPackageXml = PackageBinaryParts.withoutEmbeddedFonts(bodyOoxml.value);
        recordBulkApplySpan("body_read_fonts_removed", preparedStartedAtMs, { characters: bodyPackageXml.length });
        const publishedStartedAtMs = bulkApplyClockMs();
        this.events.publishBodyRead({ flatOpc: bodyPackageXml, paragraphUniqueLocalIds: wordParagraphs.items.map((wordParagraph) => wordParagraph.uniqueLocalId), coversEventsThroughSequence });
        recordBulkApplySpan("body_read_published", publishedStartedAtMs);
        const walkStartedAtMs = bulkApplyClockMs();
        const bodyWalk = new OoxmlRange(bodyPackageXml);
        recordBulkApplySpan("body_read_walk_built", walkStartedAtMs);
        return { wordParagraphs: wordParagraphs.items, bodyPackageXml, bodyWalk, coversEventsThroughSequence };
    }

    /**
     * One sync for the whole body: every paragraph's id and text from Office.js, and
     * every paragraph's text from the body's own OOXML through `OoxmlRange`, the
     * reader a redline's `textWhenProposed` and a tracker's remembered texts are made
     * of. The answer is Word's paragraph for each position in the walk, carrying the
     * walk's accepted text, or null where Word lists no paragraph for that position.
     *
     * WHY the walk's text and not Word's
     *   The text step compares these texts with texts that reader produced; a
     *   second reader disagrees on a hyperlink, a field or a tab, and a card
     *   whose paragraph is right there would then fall through to the server.
     *   `Paragraph.text` also reads deleted words as present under markup.
     *
     * CASES
     *   - the two lists line up (`OoxmlRange.paragraphCountLiningUpWithWord`): the same
     *     length, or the walk holding one more, the empty paragraph Word exports
     *     past the body's end                -> paired by index; that empty paragraph pairs with nothing
     *   - they differ any other way          -> paired by what each paragraph reads (`wordIndexByWalkIndexOf`),
     *                                           and one Sentry log line names both counts and how many paired
     *
     * WHY paired by text when the counts differ
     *   `Body.paragraphs` and the OOXML walk list the same paragraphs for most
     *   documents: body and cell paragraphs, in document order, none nested in
     *   another. Where they differ (the fix-check document, 20 warnings on
     *   2026-10-16), indexing Word's list by the walk's index landed a server
     *   relocation on another paragraph, and answering nothing made every Go to
     *   issue pay the server again and land nowhere. Paired by text, the paragraph
     *   the core named is the one found, and only the paragraphs one list holds
     *   alone go unanswered.
     */
    private async wordParagraphsByWalkIndex(): Promise<(WordParagraph | null)[]> {
        return OfficeWordSession.pairWalkWithWord(await this.readWordAndPackageBody());
    }

    /** Word's paragraph for each position of the walk, as `wordParagraphsByWalkIndex` describes; the pairing alone, over one body read. */
    private static pairWalkWithWord({ wordParagraphs, bodyWalk }: WordAndPackageBodyRead): (WordParagraph | null)[] {
        const acceptedTexts = bodyWalk.allChangesAcceptedTexts();
        if (bodyWalk.paragraphCountLiningUpWithWord(wordParagraphs.length) !== null) {
            /**
             * EXAMPLE
             *   Word lists 226, the walk holds 227, Word's closing paragraph last
             *   -> walk 0..225 paired with Word 0..225 by index; slice(226) leaves walk 226, which pairs with nothing
             */
            const unpairedExportParagraphs = acceptedTexts.slice(wordParagraphs.length).map(() => null);
            return [...wordParagraphs.map((wordParagraph, walkIndex) => ({ uniqueLocalId: wordParagraph.uniqueLocalId, text: acceptedTexts[walkIndex] })), ...unpairedExportParagraphs];
        }
        const wordIndexByWalkIndex = OfficeWordSession.wordIndexByWalkIndexOf(bodyWalk.textsWithDeletedWordsKept(), wordParagraphs.map((wordParagraph) => wordParagraph.text));
        const pairedParagraphs = wordIndexByWalkIndex.map((wordIndex, walkIndex) => (wordIndex === null ? null : { uniqueLocalId: wordParagraphs[wordIndex].uniqueLocalId, text: acceptedTexts[walkIndex] }));
        logWarning(BODY_PARAGRAPH_COUNT_MISMATCH_MESSAGE, {
            office_paragraph_count: wordParagraphs.length,
            ooxml_paragraph_count: acceptedTexts.length,
            paired_paragraph_count: pairedParagraphs.filter(isWordParagraph).length,
        });
        return pairedParagraphs;
    }

    /**
     * Which of Word's paragraphs each walk paragraph is, when the two lists differ in
     * length: the two lists of texts aligned by the engine's word diff over their
     * reading keys (`WordTokenizer.readingKey`, so a tab for a space or a curly quote
     * for a straight one still pairs), then paired stretch by stretch.
     *
     * CASES, per stretch of the alignment
     *   - equal                                               -> paired position by position
     *   - replaced, as many paragraphs on both sides          -> paired position by position: the same paragraphs
     *                                                            read differently by the two readers (a symbol, a
     *                                                            footnote mark)
     *   - inserted, deleted, or replaced with different counts -> unpaired: the paragraphs one list holds alone
     *
     * EXAMPLE
     *   walk ["Before", "", "Ex. 3", "After"]   Word ["Before", "Ex. 3", "After"]
     *   -> equal 0..1 | 0..1, delete 1..2 | 1..1, equal 2..4 | 1..3
     *   -> [0, null, 1, 2]: walk index 2 ("Ex. 3") is Word's paragraph 1
     *
     * RETURNS
     *   one entry per walk paragraph: the index into Word's list, or null
     */
    private static wordIndexByWalkIndexOf(walkTexts: string[], wordTexts: string[]): (number | null)[] {
        const wordIndexByWalkIndex: (number | null)[] = walkTexts.map(() => null);
        const alignment = wordDiff(walkTexts.map((walkText) => WordTokenizer.readingKey(walkText)), wordTexts.map((wordText) => WordTokenizer.readingKey(wordText)));
        for (const stretch of alignment) {
            const walkParagraphCount = stretch.beforeEnd - stretch.beforeStart;
            const wordParagraphCount = stretch.afterEnd - stretch.afterStart;
            const pairsPositionByPosition = stretch.tag === "equal" || (stretch.tag === "replace" && walkParagraphCount === wordParagraphCount);
            if (!pairsPositionByPosition) continue;
            /**
             * EXAMPLE
             *   stretch walk 2..4, Word 1..3: offset 0 pairs walk 2 with Word 1, offset 1 pairs walk 3 with Word 2
             */
            for (let offset = 0; offset < walkParagraphCount; offset += 1) wordIndexByWalkIndex[stretch.beforeStart + offset] = stretch.afterStart + offset;
        }
        return wordIndexByWalkIndex;
    }

    async bodyFlatOpc(): Promise<string> {
        return (await this.readBody()).flatOpc;
    }

    /**
     * The body's package and its paragraph ids in one sync, published on the
     * document's event stream before it is returned.
     *
     * WHY the ids ride along
     *   Loading `uniqueLocalId` costs nothing next to the package in the same
     *   sync, and it lets a follower name the paragraph behind a later Word
     *   event, which carries ids only.
     */
    async readBody(): Promise<BodyRead> {
        const coversEventsThroughSequence = this.events.lastEventSequence;
        const paragraphs = this.context.document.body.paragraphs;
        paragraphs.load("items/uniqueLocalId");
        const bodyOoxml = this.context.document.body.getOoxml();
        const wordSyncStartedAtMs = bulkApplyClockMs();
        await this.context.sync();
        recordBulkApplySpan("body_read_word_sync", wordSyncStartedAtMs, { paragraphs: paragraphs.items.length, characters: bodyOoxml.value.length });
        /** The body's package without its embedded fonts, as `readWordAndPackageBody` holds it: no reader of the page store looks at them. */
        const bodyRead: BodyRead = { flatOpc: PackageBinaryParts.withoutEmbeddedFonts(bodyOoxml.value), paragraphUniqueLocalIds: paragraphs.items.map((paragraph) => paragraph.uniqueLocalId), coversEventsThroughSequence };
        this.events.publishBodyRead(bodyRead);
        return bodyRead;
    }

    async tableFlatOpcAround(bookmarkName: string): Promise<TableRead | null> {
        const cached = this.tableReads.get(bookmarkName);
        /**
         * WHEN the table around this bookmark was read since the document last changed
         *   CONSEQUENCE
         *     that read serves again: no round trip to Word.
         */
        if (cached !== null) return cached;
        const range = this.context.document.getBookmarkRangeOrNullObject(bookmarkName);
        range.load("isNullObject");
        await this.context.sync();
        /**
         * WHEN no bookmark of that name exists
         *   CONSEQUENCE
         *     Return null; the card falls back to its text lines.
         */
        if (range.isNullObject) return null;
        const table = range.paragraphs.getFirst().parentTableOrNullObject;
        table.load("isNullObject");
        await this.context.sync();
        /**
         * WHEN the bookmarked paragraph is not inside a table
         *   CONSEQUENCE
         *     Return null; there is no table to show the row in.
         */
        if (table.isNullObject) return null;
        const tableRange = table.getRange();
        const tableOoxml = tableRange.getOoxml();
        /** Read in the same round trip as the package: the ids tell a card which paragraph changes are its table's. */
        const tableParagraphs = tableRange.paragraphs;
        tableParagraphs.load("items/uniqueLocalId");
        await this.context.sync();
        const read: TableRead = { packageOoxml: tableOoxml.value, paragraphUniqueLocalIds: tableParagraphs.items.map((paragraph) => paragraph.uniqueLocalId) };
        this.tableReads.store(bookmarkName, read);
        return read;
    }

    /**
     * Three round trips for any number of bookmarks: the bookmarks' paragraphs
     * (`readBookmarkedParagraphs`, two syncs, the document's comments riding on the
     * first), then one table read per distinct table, served from the cache when it
     * was read since the document last changed.
     */
    async readPage(bookmarkNames: string[]): Promise<PageRead> {
        const wordComments = this.queueCommentsRead();
        const paragraphs = await this.readBookmarkedParagraphs(bookmarkNames);
        const tables = new Map<string, TableRead>();
        for (const paragraph of paragraphs) {
            if (paragraph.tableId === null || tables.has(paragraph.tableId)) continue;
            const tableRead = await this.tableFlatOpcAround(paragraph.bookmarkName);
            if (tableRead !== null) tables.set(paragraph.tableId, tableRead);
        }
        /**
         * WHY the id is read as a string
         *   Office.js can hand a comment's id back as a number although its type says
         *   string, and a reply card names its target as the decimal string the agent
         *   read: keyed by the number, a comment still open would read as deleted.
         *
         * EXAMPLE
         *   Word's id 246190060 (a number) -> "246190060", the id reply card #5 names
         */
        const documentComments = wordComments.items.map((wordComment) => ({ id: String(wordComment.id), resolved: wordComment.resolved }));
        return { paragraphs, tables, documentComments };
    }

    /**
     * Queues the read of every comment's id and resolved state for the sync the
     * paragraph read makes next, so a reply card learns a resolved or deleted target
     * from the page it already watches and never from a read of its own.
     *
     * WHY no host check
     *   `Body.getComments` and `Comment.resolved` are WordApi 1.4, the least every
     *   manifest of the add-in asks of its host.
     */
    private queueCommentsRead(): Word.CommentCollection {
        const wordComments = this.context.document.body.getComments();
        wordComments.load("items/id,items/resolved");
        return wordComments;
    }

    /**
     * Every bookmark's paragraph, each read on its own: two round trips for any number
     * of bookmarks. The probes (range, first paragraph, enclosing table) and the body's
     * paragraph ids go in one sync, the paragraphs' packages and each enclosing table's
     * first paragraph in a second.
     *
     * WHY each paragraph on its own
     *   Word exports a range that crosses table rows as its first row alone (live probe,
     *   Word for Mac 16.114, 2026-10-16), so a span read cannot stand for the paragraphs
     *   of a table card; one paragraph's read always holds that paragraph.
     *
     * WHY the table's first paragraph waits for the second sync
     *   A body paragraph's `parentTableOrNullObject` is a null object, and
     *   `getRange()` is no "or null object" method: queued on a null table it can
     *   fail the sync with ItemNotFound on some hosts, and every bookmark's read with
     *   it. The first sync tells which tables exist; only those are asked for a range.
     *
     * RETURNS
     *   one read per bookmark still in the document, in the order of `bookmarkNames`,
     *   each with its paragraph's index in the body (`paragraphIndex`) for callers that
     *   need the document's order.
     */
    async readBookmarkedParagraphs(bookmarkNames: string[]): Promise<PageParagraphRead[]> {
        const bodyParagraphs = this.context.document.body.paragraphs;
        bodyParagraphs.load("items/uniqueLocalId");
        const probes = bookmarkNames.map((bookmarkName) => {
            const range = this.context.document.getBookmarkRangeOrNullObject(bookmarkName);
            const firstParagraph = range.paragraphs.getFirstOrNullObject();
            firstParagraph.load("uniqueLocalId");
            const table = firstParagraph.parentTableOrNullObject;
            table.load("isNullObject");
            return { bookmarkName, range, firstParagraph, table };
        });
        await this.context.sync();
        const indexOfParagraphId = new Map(bodyParagraphs.items.map((paragraph, index) => [paragraph.uniqueLocalId, index]));
        const found = probes
            .filter((probe) => !probe.range.isNullObject && !probe.firstParagraph.isNullObject && indexOfParagraphId.has(probe.firstParagraph.uniqueLocalId))
            .map((probe) => {
                /** A paragraph outside every table has a null table, and no table paragraph is asked for. */
                const tableFirstParagraph = probe.table.isNullObject ? null : probe.table.getRange().paragraphs.getFirstOrNullObject();
                tableFirstParagraph?.load("uniqueLocalId");
                /**
                 * WHY the paragraph is read through its whole range
                 *   Word refuses `Paragraph.getOoxml()` with GeneralException on an empty
                 *   paragraph that ends its table cell, and every bookmark is read in this
                 *   one sync, so a single blank last line of a signature block failed the
                 *   page read of every card on the page (2026-10-15). `getRange().getOoxml()`
                 *   reads every paragraph, empty cell paragraphs included (live probe, same day).
                 */
                return { ...probe, tableFirstParagraph, paragraphOoxml: probe.firstParagraph.getRange().getOoxml() };
            });
        await this.context.sync();
        return found.map((probe) => {
            /** A paragraph outside every table has no table paragraph; a table whose first paragraph did not resolve is read as none. */
            const tableId = probe.tableFirstParagraph === null || probe.tableFirstParagraph.isNullObject ? null : probe.tableFirstParagraph.uniqueLocalId;
            return {
                bookmarkName: probe.bookmarkName,
                paragraphOoxml: OfficeWordSession.bookmarkedParagraphOoxmlOf(probe.paragraphOoxml.value, probe.bookmarkName, tableId),
                uniqueLocalId: probe.firstParagraph.uniqueLocalId,
                paragraphIndex: indexOfParagraphId.get(probe.firstParagraph.uniqueLocalId) ?? Number.POSITIVE_INFINITY,
                tableId,
            };
        });
    }

    /**
     * The package a page read keeps for one bookmark's paragraph: that paragraph alone.
     *
     * CASES
     *   - a body paragraph (no table)                  -> the paragraph, the empty paragraph Word ends
     *                                                     the export with left out by Word's count of
     *                                                     the range read, one paragraph
     *                                                     (`OoxmlRange.documentParagraphsOfWordExport`)
     *   - a cell paragraph                             -> the paragraph the bookmark names, alone:
     *                                                     Word returns the WHOLE row for a cell, so
     *                                                     the first paragraph is the row's first cell
     *   - a cell read with no paragraph under the name -> the row as Word returned it
     *
     * WHY
     *   The live preview builds over the read's first paragraph and the drawer reads its
     *   text from it; with the row as read, a card on the Fee cell previewed and read over
     *   the Item cell.
     *
     * EXAMPLE
     *   row  Annual uplift cap | 3%   (bookmark _cap in the second cell)  -> the "3%" paragraph
     *   body "Customer shall pay within thirty (30) days.", then Word's empty paragraph -> the first alone
     */
    private static bookmarkedParagraphOoxmlOf(paragraphOoxml: string, bookmarkName: string, tableId: string | null): string {
        if (tableId === null) return OoxmlRange.documentParagraphsOfWordExport(paragraphOoxml, OfficeWordSession.PARAGRAPHS_IN_ONE_PARAGRAPHS_RANGE);
        return OoxmlRange.paragraphAsPackageByBookmarkName(paragraphOoxml, bookmarkName) ?? paragraphOoxml;
    }

    /** How many paragraphs Word holds in the range of one paragraph, the range a page read exports for each bookmark. */
    private static readonly PARAGRAPHS_IN_ONE_PARAGRAPHS_RANGE = 1;
}

/**
 * Where the document's event stream hears Office: Word's change events, the
 * typing detector for hosts without them, the add-in's own body replacements,
 * and the pane coming back into focus. Only `DocumentEvents` calls it, once, so
 * Word holds one set of handlers however many followers there are.
 */
class OfficeDocumentEventSource implements DocumentEventSource {
    /** Paragraph events arrived with the WordApi 1.6 requirement set; older hosts fall back to the typing detector. */
    private static readonly PARAGRAPH_EVENTS_REQUIREMENT = { set: "WordApi", version: "1.6" } as const;

    constructor(
        /** Forgotten in step with Word's reports, before any follower hears them, so no follower reads a table that changed. */
        private readonly tableReads: TableReadCache,
        private readonly editedParagraphDetector: EditedParagraphDetector,
    ) {}

    /**
     * Whether this host raises Word's paragraph events.
     *
     * WHEN Office cannot answer (no `Office.context`, a page outside Word)
     *   CONSEQUENCE
     *     false: the typing detector stands in, as on a host without the events.
     */
    wordChangesSupported(): boolean {
        try {
            return Office.context.requirements.isSetSupported(OfficeDocumentEventSource.PARAGRAPH_EVENTS_REQUIREMENT.set, OfficeDocumentEventSource.PARAGRAPH_EVENTS_REQUIREMENT.version);
        } catch {
            return false;
        }
    }

    /**
     * Word's three paragraph events and its content-control event, registered
     * under the write lock and delivered as `WordChange`s.
     *
     * CASES, for `origin`
     *   - Word names the source "Remote" -> "remote": a co-author's edit
     *   - any other source, or none      -> "local"
     */
    async registerWordChanges(handler: (change: WordChange) => void): Promise<Unsubscribe> {
        if (!this.wordChangesSupported()) throw new Error("paragraph events need WordApi 1.6, which this host does not support");
        /** Who made the change, from the source Word names on the event (`origin` CASES above). */
        const originOf = (source: Word.EventSource | "Local" | "Remote" | undefined): ChangeOrigin => (source === Word.EventSource.remote ? "remote" : "local");
        let registrations: OfficeExtension.EventHandlerResult<unknown>[] = [];
        await withWordContextWrite(async (context) => {
            /** The handler for one of Word's three paragraph events: the tables holding those paragraphs are forgotten, then the change is passed on. */
            const deliverParagraphChange = (kind: ParagraphChange["kind"]) => async (args: Word.ParagraphChangedEventArgs | Word.ParagraphAddedEventArgs | Word.ParagraphDeletedEventArgs): Promise<void> => {
                const change: ParagraphChange = { kind, uniqueLocalIds: args.uniqueLocalIds ?? [], origin: originOf(args.source) };
                this.tableReads.forgetParagraphs(change);
                handler(change);
            };
            /** The handler for a content control added: the event names no paragraph, so `forgetParagraphs` decides what it forgets. */
            const deliverContentControlAdded = async (args: Word.ContentControlAddedEventArgs): Promise<void> => {
                const change: WordChange = { kind: "content_control_added", origin: originOf(args.source) };
                this.tableReads.forgetParagraphs(change);
                handler(change);
            };
            registrations = [
                context.document.onParagraphChanged.add(deliverParagraphChange("changed")),
                context.document.onParagraphAdded.add(deliverParagraphChange("added")),
                context.document.onParagraphDeleted.add(deliverParagraphChange("deleted")),
                context.document.onContentControlAdded.add(deliverContentControlAdded),
            ];
            await context.sync();
        });
        return async () => {
            for (const registration of registrations) {
                registration.remove();
                await registration.context.sync().catch(() => undefined);
            }
            registrations = [];
        };
    }

    followEditsUnderCursor(handler: (uniqueLocalIds: string[]) => void): void {
        this.editedParagraphDetector.follow(handler);
    }

    /**
     * Word has no event for the add-in's own body replacement; `document.ts` announces
     * it around the write. Every table read is forgotten at each phase before the
     * stream hears it, as `forgetParagraphs` runs before a Word change is heard.
     */
    onBodyReplaced(handler: (phase: BodyReplacementPhase) => void): () => void {
        return BodyReplacement.subscribe((phase) => {
            this.tableReads.clear();
            handler(phase);
        });
    }

    /**
     * The pane's window gaining focus. Every table read is forgotten before the
     * stream hears it: a tracked deletion or a comment in a table raised no event
     * that would have forgotten its table.
     */
    onPaneFocused(handler: () => void): () => void {
        /** The window's focus listener: forgets every table read, then tells the stream. */
        const onWindowFocused = (): void => {
            this.tableReads.clear();
            handler();
        };
        window.addEventListener("focus", onWindowFocused);
        return () => window.removeEventListener("focus", onWindowFocused);
    }
}

export class OfficeWordDocument implements WordDocument {
    /**
     * The tables read for previews, shared by every session of this document.
     *
     * WHEN the body is replaced or a session writes
     *   CONSEQUENCE
     *     every read is forgotten, so the next card to ask reads the table as it stands.
     *
     * WHEN a paragraph changes
     *   CONSEQUENCE
     *     only the tables holding that paragraph are forgotten (`TableReadCache.forgetParagraphs`).
     */
    private readonly tableReads = new TableReadCache();

    readonly events: DocumentEvents;

    constructor(
        private readonly config: RecoveryConfig,
        editedParagraphDetector: EditedParagraphDetector,
    ) {
        this.events = new DocumentEvents(new OfficeDocumentEventSource(this.tableReads, editedParagraphDetector));
    }

    withRead<T>(fn: (session: WordSession) => Promise<T>): Promise<T> {
        return withWordContextRead((context) => fn(this.sessionFor(context)));
    }

    withWrite<T>(fn: (session: WordSession) => Promise<T>): Promise<T> {
        return withWordContextWrite(async (context) => {
            try {
                return await fn(this.sessionFor(context));
            } finally {
                this.tableReads.clear();
            }
        });
    }

    sessionFor(context: Word.RequestContext): WordSession {
        return new OfficeWordSession(context, this.config, this.tableReads, this.events);
    }

    /** Every write and every whole-document read the add-in takes is counted at the document lock. */
    pendingForegroundWork(): number {
        return pendingForegroundWorkCount();
    }
}
